import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ApiError } from '../lib/auth';
import { Button, Field, Input, Alert } from '../components/ui';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeBox = useRef<HTMLDivElement>(null);

  // The code field takes the cursor as soon as it is asked for. autoFocus
  // alone did not: React reused the password field's element for it, so it
  // never "mounted" - hence the key on it below, and this for good measure.
  useEffect(() => {
    if (needsTotp && !busy) codeBox.current?.querySelector('input')?.focus();
  }, [needsTotp, busy]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await login(email, password, needsTotp ? totpCode : undefined);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'totp_required') {
          // Step up rather than showing an error: the password was correct.
          setNeedsTotp(true);
          setError(null);
        } else {
          setError(err.message);
          if (err.code === 'totp_invalid') setTotpCode('');
        }
      } else {
        setError('Could not reach the server. Check your connection.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/icon-192.png" alt="Engage Live Cloud Hub" className="h-28 w-28 rounded-[26px] ring-1 ring-hairline" />
          <p className="mt-4 text-sm text-ink-muted">Broadcast infrastructure control</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg bg-surface p-6 ring-1 ring-hairline"
        >
          {error && <Alert tone="critical">{error}</Alert>}

          {needsTotp ? (
            <>
              <Alert tone="info">
                Enter the six-digit code from your authenticator app.
              </Alert>
              <div ref={codeBox}>
              <Field label="Authenticator code">
                <Input
                  key="totp"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={6}
                  className="text-center font-mono text-lg tracking-[0.3em]"
                />
              </Field>
              </div>
            </>
          ) : (
            <>
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                  autoComplete="username"
                />
              </Field>

              <Field label="Password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </Field>
            </>
          )}

          <Button type="submit" variant="primary" loading={busy} className="w-full">
            {needsTotp ? 'Verify' : 'Sign in'}
          </Button>

          {needsTotp && (
            <button
              type="button"
              onClick={() => {
                setNeedsTotp(false);
                setTotpCode('');
                setError(null);
              }}
              className="w-full text-center text-xs text-ink-muted hover:text-ink"
            >
              Back
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
